import { supabase } from '../lib/supabase';

class WaitlistService {
  /**
   * Add email to waitlist
   * @param {string} email - User's email
   * @param {string} source - Where they signed up (default: 'access_gate')
   * @param {string} referralCode - Optional referral code from URL
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async addToWaitlist(email, source = 'access_gate', referralCode = null) {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(normalizedEmail)) {
        return { success: false, error: 'Invalid email format' };
      }

      // Check if email already exists
      const { data: existing } = await supabase
        .from('waitlist')
        .select('id, status')
        .eq('email', normalizedEmail)
        .single();

      if (existing) {
        // Already on waitlist
        if (existing.status === 'converted') {
          return { success: false, error: 'This email already has access' };
        }
        return { success: true, alreadyExists: true };
      }

      // Insert new waitlist entry
      const { error } = await supabase
        .from('waitlist')
        .insert({
          email: normalizedEmail,
          source,
          referral_code: referralCode,
          status: 'pending'
        });

      if (error) {
        console.error('Waitlist insert error:', error);

        // Handle unique constraint violation
        if (error.code === '23505') {
          return { success: true, alreadyExists: true };
        }

        return { success: false, error: 'Failed to join waitlist' };
      }

      return { success: true };
    } catch (err) {
      console.error('WaitlistService error:', err);
      return { success: false, error: 'Something went wrong' };
    }
  }

  /**
   * Check if email is on waitlist
   * @param {string} email 
   * @returns {Promise<{onWaitlist: boolean, status?: string}>}
   */
  async checkWaitlistStatus(email) {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { data, error } = await supabase
        .from('waitlist')
        .select('status, created_at')
        .eq('email', normalizedEmail)
        .single();

      if (error || !data) {
        return { onWaitlist: false };
      }

      return {
        onWaitlist: true,
        status: data.status,
        joinedAt: data.created_at
      };
    } catch (err) {
      console.error('WaitlistService checkStatus error:', err);
      return { onWaitlist: false };
    }
  }

  /**
   * Get waitlist count (for admin/stats)
   * @returns {Promise<number>}
   */
  async getWaitlistCount() {
    try {
      const { count, error } = await supabase
        .from('waitlist')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) {
        console.error('Waitlist count error:', error);
        return 0;
      }

      return count || 0;
    } catch (err) {
      console.error('WaitlistService getCount error:', err);
      return 0;
    }
  }

  /**
   * Get all waitlist entries (admin only)
   * @param {string} status - Filter by status (optional)
   * @returns {Promise<Array>}
   */
  async getWaitlistEntries(status = null) {
    try {
      let query = supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Waitlist fetch error:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('WaitlistService getEntries error:', err);
      return [];
    }
  }

  /**
   * Update waitlist entry status (admin only)
   * @param {string} id - Waitlist entry ID
   * @param {string} status - New status
   * @returns {Promise<{success: boolean}>}
   */
  async updateStatus(id, status) {
    try {
      const updateData = { status };

      // If marking as invited, set notified_at
      if (status === 'invited') {
        updateData.notified_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('waitlist')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Waitlist update error:', error);
        return { success: false };
      }

      return { success: true };
    } catch (err) {
      console.error('WaitlistService updateStatus error:', err);
      return { success: false };
    }
  }
}

export const waitlistService = new WaitlistService();